import React, { useState, useRef, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { QRCodeCanvas } from 'qrcode.react'
import { navigate } from 'vike/client/router'
import PubSub from 'pubsub-js'
import Value from 'aldoo-ra/Value'
import { getStoreAccount } from 'aldoo-ra/Store/api'
import * as ShareAPI from './api'
import * as TusUploader from './tus-uploader'
import * as Session from './session-store'
import TextBundle from 'aldoo-ra/CMS/text-bundle'
import Button from 'aldoo-ra/Button'
import Typography from 'aldoo-ra/Typography'
import { showMessageDialog } from 'aldoo-ra/MessageDialog'
import { showPromptDialogAsync } from 'aldoo-ra/PromptDialog'
import Spinner from 'aldoo-ra/Spinner'
import UserAccessWidget from '../UserAccessWidget'
import Modal from 'aldoo-ra/Modal'
import Input from 'aldoo-ra/Input'
import { showToast, TOAST_POSITIONS } from 'aldoo-ra/Toast'
import {
  listContacts as listMyContacts,
  listGroups as listMyGroups,
  addGroup as createGroup,
  addGroupMember,
  removeGroupMember,
  addContact as addMyContact,
} from '../Contacts/controller'
import GoPremiumDialog, { openGoPremium } from '../GoPremiumDialog'
import FileTree from './FileTree'
import LinkSender from './LinkSender'
import { FeatureLockAPI } from 'aldoo-ra/FeatureLock/api'
import UploadLanding from './UploadLanding'
import Tooltip from 'aldoo-ra/Tooltip'
import ToggleSwitch from 'aldoo-ra/ToggleSwitch'
import {
  CloudArrowUpIcon,
  DocumentIcon,
  FolderIcon,
  PlusIcon,
  EnvelopeIcon,
  PencilSquareIcon,
  ChatBubbleLeftEllipsisIcon,
  LinkIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  LockClosedIcon,
  GlobeAltIcon,
  CalendarDaysIcon,
  CircleStackIcon,
  XMarkIcon,
  UsersIcon,
  ShareIcon,
  TrashIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  KeyIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowDownTrayIcon,
  SwatchIcon,
  QrCodeIcon,
} from '@heroicons/react/24/outline'
import { CheckCircleIcon } from '@heroicons/react/24/solid'
import ThemeSelect from '../ShareThemes/ThemeSelect'

const DEFAULT_RETENTION_DAYS = 3

// Files skipped on folder upload (junk/OS metadata). Matched against the
// file's basename. Extend this list as needed.
const SKIP_FILE_PATTERNS = [
  /^\.DS_Store$/i,
]
const shouldSkipFile = (path) => {
  const base = String(path).split("/").pop()
  return SKIP_FILE_PATTERNS.some((re) => re.test(base))
}

// Link durations come from the server (quota.retentionOptions, derived from the
// plan feature matrix). Styling is keyed by day with a neutral default for any
// non-standard duration an admin adds.
const RETENTION_STYLES = {
  3:  { color: 'border-blue-400',   activeColor: 'border-blue-500 bg-blue-500',     textColor: 'text-blue-500'   },
  7:  { color: 'border-orange-400', activeColor: 'border-orange-500 bg-orange-500', textColor: 'text-orange-500' },
  30: { color: 'border-green-400',  activeColor: 'border-green-500 bg-green-500',   textColor: 'text-green-500'  },
}
const RETENTION_STYLE_DEFAULT = { color: 'border-primary/40', activeColor: 'border-primary bg-primary', textColor: 'text-primary' }
const RETENTION_FALLBACK = [3, 7, 30]
const buildRetentionOptions = (days) =>
  (Array.isArray(days) && days.length ? days : RETENTION_FALLBACK)
    .map((d) => ({ days: d, label: `${d} Days`, ...(RETENTION_STYLES[d] || RETENTION_STYLE_DEFAULT) }))

const ACCESS_OPTIONS = [
  { value: 'public', labelKey: 'accessPublic', descKey: 'accessPublicDesc', icon: GlobeAltIcon },
  { value: 'authenticated', labelKey: 'accessAuthenticated', descKey: 'accessAuthenticatedDesc', icon: LockClosedIcon },
]

async function sha256hex(str) {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(str))
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('')
}

function formatBytes(bytes) {
  if (!bytes) return '0 B'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${parseFloat((bytes / (1024 * 1024 * 1024)).toFixed(2))} GB`
}

function formatSpeed(bytesPerSec) {
  if (!bytesPerSec || bytesPerSec <= 0) return ''
  if (bytesPerSec < 1024) return `${bytesPerSec.toFixed(0)} B/s`
  if (bytesPerSec < 1024 * 1024) return `${(bytesPerSec / 1024).toFixed(0)} KB/s`
  return `${(bytesPerSec / (1024 * 1024)).toFixed(1)} MB/s`
}

function formatEta(seconds) {
  if (!seconds || seconds <= 0) return ''
  if (seconds < 60) return `${Math.ceil(seconds)}s`
  const m = Math.floor(seconds / 60)
  const s = Math.ceil(seconds % 60)
  return `${m}m ${s}s`
}

export default function FileShareController({ auth, hideInfo = false }) {
  const texts = TextBundle({ id: "transfer_share" })
  const [account, setAccount] = useState(null)
  const refreshAccount = useCallback(
    () => getStoreAccount().then((res) => setAccount(res?.result || res)).catch(() => {}),
    []
  )
  useEffect(() => {
    refreshAccount()
    const token = PubSub.subscribe('account-update', () => refreshAccount())
    const token2 = PubSub.subscribe('credits-recharged', () => refreshAccount())
    const token3 = PubSub.subscribe('payment-success', () => refreshAccount())
    return () => {
      PubSub.unsubscribe(token)
      PubSub.unsubscribe(token2)
      PubSub.unsubscribe(token3)
    }
  }, [refreshAccount])

  const handleResetRef = useRef(null)

  useEffect(() => {
    const token = PubSub.subscribe('file-share-reset', () => {
      handleResetRef.current?.()
    })
    return () => PubSub.unsubscribe(token)
  }, [])

  // Mount: detect an unfinished upload from a previous session. The actual
  // file contents can't be persisted across reloads (browser sandbox), so the
  // banner asks the user to re-pick the same files; tus-js-client matches
  // them against its localStorage fingerprint cache and resumes byte-exact.
  useEffect(() => {
    const saved = Session.load()
    // Trim orphan tus fingerprints — keep only those tied to the current
    // pending session; everything else is from abandoned past uploads and
    // the server-side sweeper will have aborted their multiparts by now.
    TusUploader.pruneOrphanFingerprints({
      keepOrderIDs: new Set(saved?.orderID ? [saved.orderID] : []),
    })
    if (!saved) return
    setPendingSession(saved)
    pendingSessionRef.current = saved
    // Pre-restore the share settings so the user lands on their previous
    // configuration — only the file pickers need re-engagement.
    const s = saved.settings || {}
    if (s.retentionDays != null) setRetentionDays(s.retentionDays)
    if (s.accessType) setAccessType(s.accessType)
    if (Array.isArray(s.accessEmails)) setAccessEmails(s.accessEmails)
    if (typeof s.notes === 'string') setNotes(s.notes)
    if (typeof s.title === 'string') setTitle(s.title)
    if (s.downloadLimit != null) setDownloadLimit(s.downloadLimit)
    if (s.passwordEnabled) setPasswordEnabled(true)
    setOrderID(saved.orderID)
  }, [])

  const [orderID, setOrderID] = useState(null)
  const [files, setFiles] = useState([])
  // Resumable-upload session restored from localStorage (set on mount when
  // the previous tab/session was killed mid-upload). `pendingSessionRef`
  // mirrors it so the handleShare closure can read the latest value without
  // triggering a re-render.
  const [pendingSession, setPendingSession] = useState(null)
  const pendingSessionRef = useRef(null)
  // True once the user clicks "Resume" on the banner → render the full
  // share form pre-populated with their previous settings and an expected-
  // files list, so they can re-attach the matching File objects.
  const [resumeMode, setResumeMode] = useState(false)
  const [showVerifyPrompt, setShowVerifyPrompt] = useState(false)

  // Track viewport so mobile guests get a verify-only screen instead of the
  // share form (mobile OAuth flows full-page-redirect and would wipe in-progress
  // state). Tailwind's lg breakpoint is 1024px.
  const [isMobile, setIsMobile] = useState(() => (
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(max-width: 1023px)").matches
      : false
  ))
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return
    const mq = window.matchMedia("(max-width: 1023px)")
    const onChange = (e) => setIsMobile(e.matches)
    mq.addEventListener?.("change", onChange) || mq.addListener?.(onChange)
    return () => {
      mq.removeEventListener?.("change", onChange) || mq.removeListener?.(onChange)
    }
  }, [])
  const [creating, setCreating] = useState(false)
  const [paused, setPaused] = useState(false)
  // Distinguishes a pause-driven abort (keep state, allow Resume) from a
  // cancel-driven abort (delete order, wipe session). Read in handleShare's
  // finally block so the caller doesn't have to thread it through.
  const pauseRequestedRef = useRef(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadSpeed, setUploadSpeed] = useState(0)
  const [uploadEta, setUploadEta] = useState(null)
  // Per-file upload state — fed into FileTree as progressMap + statusMap so
  // each row shows its own progress bar while the share is being created.
  const [fileProgress, setFileProgress] = useState({})  // path → 0..1
  const [fileStatus, setFileStatus] = useState({})       // path → 'uploading' | 'done'
  // The single path currently being PATCHed — drives the FileTree's auto-
  // scroll-to-top. Object.keys(fileStatus).find(uploading) would return in
  // insertion order, not actual upload order, so it lands on the wrong row.
  const [activeUploadPath, setActiveUploadPath] = useState(null)
  const [gcsProgress, setGcsProgress] = useState(null)
  const uploadStartRef = useRef(null)
  const lastSampleRef = useRef(null)
  const smoothedSpeedRef = useRef(0)
  const gcsPollRef = useRef(null)
  const uploadAbortRef = useRef(null)
  const [completed, setCompleted] = useState(false)
  const [shareToken, setShareToken] = useState(null)
  const [shareResult, setShareResult] = useState(null)
  const [retentionDays, setRetentionDays] = useState(DEFAULT_RETENTION_DAYS)
  const [quota, setQuota] = useState(null) // { maxSize, volumeLimit, countLimit, volumeUsed, countUsed, maxRetentionDays, retentionOptions }
  const maxRetention = quota?.maxRetentionDays ?? DEFAULT_RETENTION_DAYS
  const retentionOptions = buildRetentionOptions(quota?.retentionOptions)

  // Keep the selection valid as options/cap arrive (snap to first allowed).
  useEffect(() => {
    const days = retentionOptions.map((o) => o.days)
    if (days.length && (!days.includes(retentionDays) || retentionDays > maxRetention)) {
      setRetentionDays(days.find((d) => d <= maxRetention) ?? days[0])
    }
  }, [quota]) // eslint-disable-line react-hooks/exhaustive-deps
  // Read the "Send Now" hand-off synchronously during state init so the very
  // first render already shows the share form + recipients (no landing flash).
  const initialAccess = (() => {
    if (typeof window === 'undefined') return null
    try {
      const raw = window.sessionStorage.getItem('transfer:initialAccess')
      if (!raw) return null
      const parsed = JSON.parse(raw)
      if (!Array.isArray(parsed?.emails) || !parsed.emails.length) return null
      return {
        emails: parsed.emails.map((e) => String(e).toLowerCase()),
        type: parsed.type || 'authenticated',
      }
    } catch { return null }
  })()

  const [accessType, setAccessType] = useState(initialAccess?.type || 'public')
  const [accessEmails, setAccessEmails] = useState(initialAccess?.emails || [])
  const [showAccessDrawer, setShowAccessDrawer] = useState(false)
  const [activePanel, setActivePanel] = useState(null)
  // When set, the share form renders even with no files yet (Send-Now flow).
  const [sendNowMode, setSendNowMode] = useState(!!initialAccess)

  // Clear the hand-off after mount so a refresh starts clean.
  useEffect(() => {
    if (!initialAccess) return
    try { window.sessionStorage.removeItem('transfer:initialAccess') } catch {}
  }, [])
  const [drawerContacts, setDrawerContacts] = useState([])
  const [drawerGroups, setDrawerGroups] = useState([])
  const [maxAccessEmails, setMaxAccessEmails] = useState(0)

  // Account-level cap on access recipients (feature-lock).
  useEffect(() => {
    FeatureLockAPI.getFeatureParam('max-share-recipients', 'value')
      .then((v) => { const n = parseInt(v, 10); if (Number.isFinite(n) && n > 0) setMaxAccessEmails(n) })
      .catch(() => {})
  }, [])
  const [saveGroupOpen, setSaveGroupOpen] = useState(false)
  const [saveGroupName, setSaveGroupName] = useState('')
  const [savingGroup, setSavingGroup] = useState(false)

  // Load contacts + groups whenever the access drawer opens (used for the
  // Save-as-group flow that maps emails -> userIds and detects name collisions).
  useEffect(() => {
    if (!showAccessDrawer) return
    let alive = true
    listMyContacts()
      .then((res) => { if (alive && res?.result) setDrawerContacts(res.contacts || []) })
      .catch(() => {})
    listMyGroups()
      .then((res) => { if (alive && res?.result) setDrawerGroups(res.groups || []) })
      .catch(() => {})
    return () => { alive = false }
  }, [showAccessDrawer])

  const handleClearAccessAll = async () => {
    if (!accessEmails.length) return
    const ok = await showPromptDialogAsync({
      title: texts?.clearAll,
      message: texts?.clearAllConfirm,
      yesLabel: texts?.clear,
      noLabel: texts?.cancel,
    })
    if (ok) setAccessEmails([])
  }

  const handleSubmitSaveGroup = async () => {
    const name = saveGroupName.trim()
    if (!name || !accessEmails.length) return
    // Groups store userIds, so resolve every access email to a userId. Missing
    // ones are auto-added as manual contacts (userId: `email:<email>`, matching
    // AddContactDialog's manual flow) so the group ends up complete.
    setSavingGroup(true)
    let contactsSnapshot = drawerContacts
    const userIds = []
    try {
      for (const e of accessEmails) {
        const existing = contactsSnapshot.find((c) => c.email?.toLowerCase() === e)
        if (existing) { userIds.push(existing.userId); continue }
        const newId = `email:${e}`
        try {
          const res = await addMyContact({ userId: newId, name: e, email: e, avatar: null })
          const created = res?.contact || { userId: newId, name: e, email: e, avatar: null }
          contactsSnapshot = [...contactsSnapshot, created]
          userIds.push(created.userId)
        } catch {
          userIds.push(newId)
          contactsSnapshot = [...contactsSnapshot, { userId: newId, name: e, email: e, avatar: null }]
        }
      }
      setDrawerContacts(contactsSnapshot)
    } finally {
      setSavingGroup(false)
    }

    const existing = drawerGroups.find((g) => (g.name || '').toLowerCase() === name.toLowerCase())

    if (existing) {
      const ok = await showPromptDialogAsync({
        title: texts?.overwriteGroup,
        message: `${texts?.overwriteGroupConfirm} "${existing.name}"?`,
        yesLabel: texts?.yes,
        noLabel: texts?.no,
      })
      if (!ok) return
      setSavingGroup(true)
      try {
        const current = existing.members || []
        const toRemove = current.filter((id) => !userIds.includes(id))
        const toAdd = userIds.filter((id) => !current.includes(id))
        for (const id of toRemove) await removeGroupMember(existing.groupId, id)
        for (const id of toAdd) await addGroupMember(existing.groupId, id)
        setDrawerGroups((prev) => prev.map((g) =>
          g.groupId === existing.groupId ? { ...g, members: userIds } : g
        ))
        setSaveGroupOpen(false)
        setSaveGroupName('')
      } finally {
        setSavingGroup(false)
      }
      return
    }

    setSavingGroup(true)
    try {
      const res = await createGroup(name)
      const created = res?.group
      if (!created) return
      for (const id of userIds) await addGroupMember(created.groupId, id)
      setDrawerGroups((prev) => [...prev, { ...created, members: userIds }])
      setSaveGroupOpen(false)
      setSaveGroupName('')
      showToast({
        position: TOAST_POSITIONS.TOP_CENTER,
        variant: 'success',
        duration: 3000,
        enableDismissalTracking: false,
        styleOverrides: { progressContainer: 'hidden' },
        title: created.name,
        message: `${userIds.length} ${userIds.length === 1 ? texts?.member : texts?.members}`,
      })
    } finally {
      setSavingGroup(false)
    }
  }
  const [notes, setNotes] = useState('')
  const [title, setTitle] = useState('')
  const [accessTab, setAccessTab] = useState('duration') // duration|title|access|password|notes|limit
  // Tab click handler — clicking the currently-active tab collapses its
  // content (sets the active tab to null), so the tabs row stays visible
  // but the form below disappears. Used during upload to clear the form
  // chrome and focus the user on progress.
  const toggleAccessTab = (tab) => setAccessTab((prev) => (prev === tab ? null : tab))
  const [downloadLimit, setDownloadLimit] = useState('')
  // Selected share theme id (DEFAULT_THEME_ID for the built-in look). ThemeSelect
  // resolves the user's default theme on first load.
  const [selectedThemeId, setSelectedThemeId] = useState(null)
  const [passwordEnabled, setPasswordEnabled] = useState(false)
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [copied, setCopied] = useState(false)

  const addFileInputRef = useRef(null)
  const addFolderInputRef = useRef(null)
  const resumeInputRef = useRef(null)
  // Dedicated folder picker used by the per-folder "Find" action in resume
  // mode. `findFolderTargetRef` holds the saved folder path the user wants
  // to re-attach, so when the picker resolves we can trim webkitRelativePath
  // back to the saved namespace before matching.
  const findFolderInputRef = useRef(null)
  const findFolderTargetRef = useRef(null)
  const tabsScrollRef = useRef(null)
  const suppressBeforeUnloadRef = useRef(false)

  // Banner Resume → enter resume mode. The full share form renders with the
  // user's previous settings restored and an expected-files list; the user
  // re-attaches matching File objects via the same Add Files button used in
  // the normal flow. Each picked file is matched by fingerprint against the
  // saved list — matched ones go into `files` state; non-matches are
  // surfaced as "extra" so the user knows they don't belong.
  const enterResumeMode = () => {
    setResumeMode(true)
  }

  // Per-folder Find action. Stash the saved folder path the user clicked on,
  // then trigger the directory picker. When it resolves, the change handler
  // remaps webkitRelativePath onto the saved namespace (the user may have
  // renamed the top-level folder between sessions) and runs the standard
  // match flow.
  const handleFindFolder = (savedFolderPath) => {
    if (creating) return
    findFolderTargetRef.current = savedFolderPath
    findFolderInputRef.current?.click()
  }

  const handleFindFolderPick = (fileList) => {
    const target = findFolderTargetRef.current
    findFolderTargetRef.current = null
    if (!fileList?.length) return
    const arr = Array.from(fileList)
    // Strip the picked top-level folder name and prepend the saved one, so
    // paths line up with what's stored in pendingSession.files even if the
    // user renamed the directory after the previous session.
    const pickedTop = arr[0].webkitRelativePath?.split('/')[0] || ''
    const picked = arr.map(f => {
      const rel = f.webkitRelativePath || f.name
      let path = rel
      if (target && pickedTop && rel.startsWith(pickedTop + '/')) {
        path = target + '/' + rel.slice(pickedTop.length + 1)
      } else if (target) {
        path = target + '/' + rel
      }
      return { file: f, path }
    })
    addEntries(picked)
  }

  const handleResumePick = (fileList) => {
    if (!pendingSession) return
    const picked = Array.from(fileList || []).map(f => ({
      file: f, path: f.webkitRelativePath || f.name,
    }))
    // Treat picks like any other Add — saved-matching files satisfy the
    // expected list, others get added as new entries the user explicitly
    // wants in this share too. addEntries already dedupes by path+size.
    addEntries(picked)
  }

  // In resumeMode, removing a file/folder also prunes the matching entry
  // from the saved-session expected list — otherwise the picker would still
  // demand it. Persists the trimmed list to localStorage so a refresh keeps
  // the same shape.
  const removeByPathResume = async (path, isFolder) => {
    if (creating) return
    const label = path.split('/').pop()
    const confirmed = await showPromptDialogAsync({
      title: isFolder ? texts?.removeFolder : texts?.removeFile,
      message: `${texts?.remove} "${label}" ${texts?.fromThisShareQ}`,
      yesLabel: texts?.remove,
      noLabel: texts?.cancel,
    })
    if (!confirmed) return
    const prefix = path + '/'
    const keep = (p) => isFolder ? !(p === path || p.startsWith(prefix)) : p !== path
    setFiles((prev) => prev.filter((e) => keep(e.path)))
    setPendingSession((ps) => {
      if (!ps) return ps
      const filtered = ps.files.filter((f) => keep(f.path))
      if (filtered.length === 0) {
        // No saved files left → drop resume mode, behave like a fresh share.
        Session.clear()
        pendingSessionRef.current = null
        setResumeMode(false)
        return null
      }
      const next = { ...ps, files: filtered }
      pendingSessionRef.current = next
      Session.update({ files: filtered })
      return next
    })
  }

  const handleDiscardSession = async () => {
    const ok = await showPromptDialogAsync({
      title: texts?.discardResume,
      message: texts?.discardResumeDesc,
      yesLabel: texts?.discard,
      noLabel: texts?.cancel,
    })
    if (!ok) return
    if (pendingSession?.orderID) {
      ShareAPI.DeleteFileShare(pendingSession.orderID).catch(() => {})
    }
    Session.clear()
    setPendingSession(null)
    pendingSessionRef.current = null
    setOrderID(null)
    setResumeMode(false)
    setFiles([])
  }

  const hasFiles = files.length > 0
  const totalSize = files.reduce((sum, e) => sum + (e.file?.size || 0), 0)

  // Email verification gate for guests: they must sign in (verify) before a
  // share can be created. Logged-in users never see this.
  const [profile] = Value("user-profile")
  const [wasGuest, setWasGuest] = useState(false)
  useEffect(() => { if (auth === null) setWasGuest(true) }, [auth])
  const verified = !!auth
  const verifiedEmail = profile?.email || account?.email || ''

  const openSignIn = () => {
    PubSub.publish("signin", { redirect: '/file-share', forceDialog: true })
  }
  // Per-transfer size limit comes from the plan quota (fallback 3 GB free tier).
  const maxFileSize = quota?.maxSize ?? 3 * 1024 ** 3

  // Client-side plan check — same shape iOS uses. Returns the reason copy
  // to surface as the GoPremium headline if the current transfer would
  // exceed any plan limit (file size / count / volume / retention). Each
  // string mirrors the server's QUOTA_ERRORS message so the dialog reads
  // the same whether the gate hit pre- or post-flight.
  const quotaPreflightReason = () => {
    const q = quota
    if (!q) return null
    const totalSize = files.reduce((s, e) => s + (e?.file?.size || 0), 0)
    if (q.maxSize > 0 && files.some((e) => (e?.file?.size || 0) > q.maxSize)) {
      return texts?.fileTooLargePlan
    }
    if (Number.isFinite(q.countLimit) && q.countLimit > 0
        && Number.isFinite(q.countUsed) && q.countUsed + 1 > q.countLimit) {
      return texts?.transferCountExceeded
    }
    if (Number.isFinite(q.volumeLimit) && q.volumeLimit > 0
        && Number.isFinite(q.volumeUsed) && q.volumeUsed + totalSize > q.volumeLimit) {
      return texts?.volumeExceeded
    }
    if (Number.isFinite(q.maxRetentionDays) && q.maxRetentionDays > 0
        && retentionDays > q.maxRetentionDays) {
      return texts?.retentionLocked
    }
    return null
  }

  const loadQuota = useCallback(() => {
    ShareAPI.GetQuota().then((q) => { if (q?.result) setQuota(q) }).catch(() => {})
  }, [])

  // Load the plan's transfer limits + rolling 30-day usage. Runs for guests
  // too — the server returns Free-plan limits so the retention picker is driven
  // by the feature matrix (single source of truth) instead of a client default.
  useEffect(() => {
    if (auth === undefined) return // session still resolving
    loadQuota()
    const t = PubSub.subscribe('payment-success', () => loadQuota())
    return () => PubSub.unsubscribe(t)
  }, [auth, loadQuota])

  // Warn before closing/reloading the tab while an upload is in progress
  useEffect(() => {
    if (!hasFiles || completed) return
    const handler = (e) => {
      if (suppressBeforeUnloadRef.current) return
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [hasFiles, completed])

  const selectedRetention = retentionOptions.find((o) => o.days === retentionDays)

  const createOrder = async () => {
    const response = await ShareAPI.CreateOrder('file-share')
    if (response.error) throw new Error(response.error)
    setOrderID(response.orderID)
    return response.orderID
  }

  // Recursively read a dropped folder entry into { file, path } records,
  // preserving the relative path so the folder tree can be reconstructed.
  const readEntry = (entry, base = "") => new Promise((resolve) => {
    if (entry.isFile) {
      entry.file(
        (file) => resolve([{ file, path: base + entry.name }]),
        () => resolve([])
      )
    } else if (entry.isDirectory) {
      const reader = entry.createReader()
      const collected = []
      const readBatch = () => reader.readEntries(async (batch) => {
        if (!batch.length) {
          const nested = await Promise.all(collected.map((e) => readEntry(e, base + entry.name + "/")))
          resolve(nested.flat())
        } else {
          collected.push(...batch)
          readBatch()
        }
      }, () => resolve([]))
      readBatch()
    } else {
      resolve([])
    }
  })

  const handleFileDrop = (e) => {
    e.preventDefault()
    if (creating) return
    const items = e.dataTransfer?.items
    // Folder-aware path: read directory entries when the browser supports it.
    if (items?.length && items[0]?.webkitGetAsEntry) {
      const roots = []
      for (const it of items) {
        const en = it.webkitGetAsEntry?.()
        if (en) roots.push(en)
      }
      if (roots.length) {
        Promise.all(roots.map((en) => readEntry(en))).then((r) => addEntries(r.flat()))
        return
      }
    }
    if (e.dataTransfer?.files?.length) {
      addEntries(Array.from(e.dataTransfer.files).map((f) => ({ file: f, path: f.webkitRelativePath || f.name })))
    }
  }

  // From an <input> FileList — webkitRelativePath is set for directory pickers.
  const handleFilesSelected = (fileList) => {
    addEntries(Array.from(fileList || []).map((f) => ({ file: f, path: f.webkitRelativePath || f.name })))
  }

  const addEntries = (rawIncoming) => {
    if (creating) return
    // Drop OS junk (e.g. .DS_Store) before anything else.
    const incoming = (rawIncoming || []).filter((e) => !shouldSkipFile(e.path))
    if (!incoming.length) return

    const tooBig = incoming.find((e) => e.file.size > maxFileSize)
    if (tooBig) {
      showMessageDialog({
        title: texts?.fileTooLarge,
        message: <span>{texts?.eachFileLessThan} <strong>{formatBytes(maxFileSize)}</strong>.</span>,
      })
      return
    }

    // Add new entries, skipping ones already in the list (matched by path + size).
    setFiles((prev) => {
      const seen = new Set(prev.map((e) => `${e.path}:${e.file.size}`))
      const merged = [...prev]
      for (const e of incoming) {
        const key = `${e.path}:${e.file.size}`
        if (!seen.has(key)) { merged.push(e); seen.add(key) }
      }
      return merged
    })
  }

  // Remove a single file (exact path) or a whole folder (path + everything under it).
  const removeByPath = async (path, isFolder) => {
    if (creating) return
    const label = path.split("/").pop()
    const confirmed = await showPromptDialogAsync({
      title: isFolder ? texts?.removeFolder : texts?.removeFile,
      message: `${texts?.remove} "${label}" ${texts?.fromThisShareQ}`,
      yesLabel: texts?.remove,
      noLabel: texts?.cancel,
    })
    if (!confirmed) return
    const prefix = path + "/"
    setFiles((prev) => prev.filter((e) =>
      isFolder ? !(e.path === path || e.path.startsWith(prefix)) : e.path !== path
    ))
  }

  const handleShare = async () => {
    if (!hasFiles) return

    // Plan pre-flight — run BEFORE createOrder / upload / finalize so the
    // user doesn't spend bandwidth on a transfer the plan won't allow.
    // Mirrors the iOS SendScreen quotaPreflightReason path. Skip on resume
    // (order already minted; uploaded bytes are in flight and the file set
    // can't change anyway).
    const isResumeStart = paused || !!pendingSessionRef.current
    if (!isResumeStart) {
      const reason = quotaPreflightReason()
      if (reason) {
        openGoPremium(reason)
        return
      }
    }

    setCreating(true)
    // Collapse any open form tab (Duration / Title / Notes / Access / Password
    // / Limit) so only the tab row remains visible above the progress UI.
    setAccessTab(null)
    // Only zero the total progress on a fresh start. On Pause→Resume OR on
    // post-refresh resume (pendingSession was loaded from localStorage) keep
    // the last known value — tus emits onProgress(0) before its resume HEAD
    // lands and the monotonic guard then holds the bar until a real offset
    // arrives. Without this, every resume snaps the total bar back to 0.
    const isResume = paused || !!pendingSessionRef.current
    if (!isResume) {
      setUploadProgress(0)
      setUploadSpeed(0)
      setUploadEta(null)
    }
    uploadStartRef.current = Date.now()
    lastSampleRef.current = { time: Date.now(), loaded: 0 }
    smoothedSpeedRef.current = 0
    const abortController = new AbortController()
    uploadAbortRef.current = abortController

    let currentOrderID = null
    try {
      // Reuse the existing order if a previous session was resumed; otherwise
      // mint a new one. Resuming preserves the orderID so any chunks already
      // committed on R2 (tracked by tus-js-client's per-fingerprint URLs) keep
      // their offsets and we don't restart from zero.
      currentOrderID = pendingSessionRef.current?.orderID || orderID || (await createOrder())
      if (!orderID) setOrderID(currentOrderID)

      // On resume: pull the list of files already committed on the server so
      // uploadMany can skip them. Folder uploads often have many small files
      // that complete fully between Pause and Refresh — without this, every
      // refresh would re-PATCH them from 0 (tus stores fingerprints
      // per-browser only).
      let skipKeys = new Set()
      if (pendingSessionRef.current) {
        try {
          const st = await ShareAPI.GetOrderStatus(currentOrderID, true)
          if (Array.isArray(st?.files)) {
            for (const f of st.files) skipKeys.add(`${f.name}:${f.size}`)
            // Pre-mark these as done so the FileTree reflects the server's
            // state immediately, before the first real onProgress fires.
            setFileStatus((prev) => {
              const next = { ...prev }
              for (const f of st.files) next[f.name] = 'done'
              return next
            })
            setFileProgress((prev) => {
              const next = { ...prev }
              for (const f of st.files) next[f.name] = 1
              return next
            })
          }
        } catch {}
      }

      // Persist enough context to recover the session on a refresh / tab close.
      // The actual byte-level resume comes from tus-js-client's urlStorage,
      // keyed by file fingerprint — this just records which order + settings
      // the in-progress uploads belong to.
      Session.save({
        orderID: currentOrderID,
        entries: files,
        settings: {
          retentionDays, accessType, accessEmails, notes, title, downloadLimit,
          passwordEnabled,
        },
      })
      pendingSessionRef.current = null

      const onProgress = (loaded, total) => {
        const now = Date.now()
        const pct = total > 0 ? Math.round((loaded / total) * 100) : 0
        // Monotonic — tus emits onProgress(0) before its resume HEAD lands;
        // would otherwise snap the total bar back to 0 on every Resume.
        setUploadProgress((prev) => (pct < prev ? prev : pct))

        const last = lastSampleRef.current
        const dt = (now - last.time) / 1000
        if (dt >= 0.5) {
          const instantSpeed = (loaded - last.loaded) / dt
          const alpha = smoothedSpeedRef.current === 0 ? 1 : 0.25
          const smoothed = alpha * instantSpeed + (1 - alpha) * smoothedSpeedRef.current
          smoothedSpeedRef.current = smoothed
          setUploadSpeed(smoothed)
          const remaining = total - loaded
          setUploadEta(smoothed > 0 ? remaining / smoothed : null)
          lastSampleRef.current = { time: now, loaded }
        }
      }

      // Mark all queued files as uploading. Keep any prior progress values
      // (e.g. after Pause→Resume) so the bars don't snap back to 0 while tus
      // re-queries the server for the committed offset; they'll update as
      // soon as the first onProgress callback fires.
      setFileStatus((prev) => {
        const next = { ...prev }
        for (const e of files) if (next[e.path] !== 'done') next[e.path] = 'uploading'
        return next
      })
      setFileProgress((prev) => {
        const next = { ...prev }
        for (const e of files) if (next[e.path] == null) next[e.path] = 0
        return next
      })

      // Upload top-to-bottom matching the FileTree's render order
      // (lexicographic path → groups each folder's files contiguously and
      // alpha-orders within). Otherwise the user sees random files starting,
      // which feels chaotic in a long folder.
      const ordered = [...files].sort((a, b) => a.path.localeCompare(b.path))

      // One sequential TUS upload per file. Per-file resumability is automatic:
      // tus-js-client stores the upload URL per file fingerprint in localStorage,
      // so re-picking the same file in a future session (after a tab close /
      // refresh / network drop) continues from the committed offset on R2.
      // `skipKeys` lists files already committed server-side — uploadMany
      // surfaces them as done without re-issuing the PATCH.
      await TusUploader.uploadMany({
        orderID: currentOrderID,
        entries: ordered,
        skipKeys,
        onProgress,
        onFileProgress: (path, sent, total) => {
          setActiveUploadPath((prev) => (prev === path ? prev : path))
          setFileProgress((prev) => {
            const pct = total > 0 ? sent / total : 0
            // Monotonic: tus-js-client emits onProgress(0, total) before its
            // resume HEAD lands, which would otherwise snap a partially-
            // uploaded bar back to 0. Hold the last-known value until a
            // real (higher) offset comes in.
            if (pct < (prev[path] || 0)) return prev
            return { ...prev, [path]: pct }
          })
        },
        onFileDone: (path) => {
          setFileProgress((prev) => ({ ...prev, [path]: 1 }))
          setFileStatus((prev) => ({ ...prev, [path]: 'done' }))
        },
        signal: abortController.signal,
      })
      setUploadProgress(100)

      const passwordHash = passwordEnabled && password ? await sha256hex(password) : null
      const limitNum = parseInt(downloadLimit, 10)
      const result = await ShareAPI.ProcessFileShare(currentOrderID, { retentionDays, accessType, access: accessEmails, notes, password: passwordHash, title, downloadLimit: limitNum > 0 ? limitNum : null, themeId: selectedThemeId })

      // Plan quota errors — explain + point to upgrade, don't treat as a hard failure.
      const QUOTA_ERRORS = {
        file_too_large: texts?.fileTooLargePlan,
        volume_exceeded: texts?.volumeExceeded,
        transfer_count_exceeded: texts?.transferCountExceeded,
        retention_locked: texts?.retentionLocked,
      }
      if (result.error && QUOTA_ERRORS[result.error]) {
        setCreating(false)
        // Surface the quota hit through the same GoPremium dialog used
        // everywhere else; the specific reason becomes the dialog headline
        // so the user sees what they hit (volume / count / retention /
        // file size) instead of a generic "Upgrade required" prompt.
        openGoPremium(QUOTA_ERRORS[result.error])
        return
      }
      if (result.error) throw new Error(result.error)

      await refreshAccount()
      loadQuota()
      setShareToken(result.share_token)
      setShareResult(result)
      setCompleted(true)
      // Share is live → drop the in-progress session record and the per-file
      // tus fingerprints; both are now garbage.
      Session.clear()
      TusUploader.discardFingerprints(files.map(e => e.file)).catch(() => {})
      window.scrollTo({ top: 0, behavior: "instant" })
    } catch (err) {
      if (!abortController.signal.aborted) {
        showMessageDialog({
          title: texts?.error,
          message: err.message || texts?.failedCreateLink,
        })
        // Hard error (not a user cancel / pause): order is unrecoverable.
        // Drop the saved session so the resume banner doesn't reappear.
        if (currentOrderID) {
          ShareAPI.DeleteFileShare(currentOrderID).catch(() => {})
          setOrderID(null)
          Session.clear()
          TusUploader.discardFingerprints(files.map(e => e.file)).catch(() => {})
        }
      }
      // On a clean abort (cancel button OR tab-close mid-flight) we KEEP the
      // saved session — that's the whole point of resumability. The order is
      // still pending on the server, tus fingerprints remain valid.
    } finally {
      uploadAbortRef.current = null
      if (gcsPollRef.current) {
        clearInterval(gcsPollRef.current)
        gcsPollRef.current = null
      }
      setGcsProgress(null)
      setCreating(false)
      // Pause-driven abort: flip into paused UI so Resume can re-run handleShare.
      // tus-js-client kept its URLs (abort(false)), so tus.Upload.findPreviousUploads
      // resumes each file at its committed offset on the next call.
      if (pauseRequestedRef.current) {
        pauseRequestedRef.current = false
        setPaused(true)
      }
    }
  }

  const handlePauseUpload = () => {
    if (!uploadAbortRef.current) return
    pauseRequestedRef.current = true
    uploadAbortRef.current.abort()
  }

  const handleResumeUpload = () => {
    setPaused(false)
    handleShare()
  }

  const handleCancelCreate = async () => {
    const confirmed = await showPromptDialogAsync({
      title: texts?.cancel,
      message: texts?.cancelCreateShare,
    })
    if (!confirmed) return
    uploadAbortRef.current?.abort()
    if (orderID) {
      ShareAPI.DeleteFileShare(orderID).catch(() => {})
      setOrderID(null)
    }
    // Explicit user cancel → wipe persistence so no resume banner appears.
    Session.clear()
    TusUploader.discardFingerprints(files.map(e => e.file)).catch(() => {})
    setPaused(false)
    setFileProgress({})
    setFileStatus({})
  }

  const getShareUrl = () => {
    if (typeof window === 'undefined') return ''
    return `${window.location.origin}/s/${shareToken}`
  }

  const handleCopy = async () => {
    const url = getShareUrl()
    try {
      await navigator.clipboard.writeText(url)
    } catch {
      try {
        const ta = document.createElement("textarea")
        ta.value = url
        ta.style.cssText = "position:fixed;opacity:0;pointer-events:none"
        document.body.appendChild(ta)
        ta.focus()
        ta.select()
        document.execCommand("copy")
        document.body.removeChild(ta)
      } catch {}
    }
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleNativeShare = () => {
    const url = getShareUrl()
    if (!navigator.share) {
      handleCopy()
      return
    }
    try {
      navigator.share({ url }).catch(() => handleCopy())
    } catch {
      handleCopy()
    }
  }

  const handleDownload = async () => {
    const result = await ShareAPI.GetShareDownloadUrl(shareToken)
    if (result?.url) window.open(result.url, '_blank')
  }

  const handleReset = () => {
    // Only discard the order if the share was never completed — a completed
    // order is a live share and must be kept.
    if (orderID && !completed) {
      ShareAPI.DeleteFileShare(orderID).catch(() => {})
      TusUploader.discardFingerprints(files.map(e => e.file)).catch(() => {})
    }
    Session.clear()
    setPendingSession(null)
    pendingSessionRef.current = null
    setResumeMode(false)
    setPaused(false)
    setFileProgress({})
    setFileStatus({})
    setActiveUploadPath(null)
    setFiles([])
    setOrderID(null)
    setUploadProgress(0)
    setUploadSpeed(0)
    setUploadEta(null)
    setGcsProgress(null)
    setCompleted(false)
    setShareToken(null)
    setShareResult(null)
    setCopied(false)
    setAccessEmails([])
    setShowAccessDrawer(false)
    setNotes('')
    setTitle('')
    setDownloadLimit('')
    setAccessTab('duration')
    setRetentionDays(DEFAULT_RETENTION_DAYS)
    setAccessType('public')
    setPasswordEnabled(false)
    setPassword('')
    setShowPassword(false)
  }
  handleResetRef.current = handleReset

  // Back button: reset the form and, if we landed here from a contact/group
  // "send now" hand-off, return to the Contacts tab instead of just clearing state.
  const handleBack = () => {
    const cameFromContacts = sendNowMode
    handleReset()
    if (cameFromContacts) navigate('/account/contacts')
  }

  // Upload/creation status — shown in place of the Create button while creating.
  const renderCreateProgress = () => (
    <div className="w-full">
      <div className="h-1.5 bg-bordered rounded-full overflow-hidden">
        {uploadProgress < 100 ? (
          <div className="h-full bg-blue-500 rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
        ) : (
          <div className="h-full w-full bg-blue-400 rounded-full origin-left animate-[pulse_1.5s_ease-in-out_infinite]" />
        )}
      </div>
      {uploadProgress < 100 && (
        <div className="flex justify-between mt-1.5 text-xs text-text_secondary">
          <span>{formatSpeed(uploadSpeed)}</span>
          {uploadEta != null && <span>{formatEta(uploadEta)} {texts?.remaining}</span>}
        </div>
      )}
      <div className="mt-6 flex items-center justify-between gap-3">
        <div className="flex items-center gap-1.5 text-sm text-text_secondary">
          {!paused && <Spinner variant="minimal" />}
          <span className="font-medium">
            {paused
              ? `${texts?.paused} · ${uploadProgress}%`
              : (uploadProgress >= 100 ? texts?.creatingLink : `${uploadProgress}%`)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleCancelCreate} variant="secondary_red_s" className="text-sm px-3 h-8">
            {texts?.cancel}
          </Button>
          {paused ? (
            <Button onClick={handleResumeUpload} variant="primary_s" className="text-sm px-3 h-8">
              {texts?.resume}
            </Button>
          ) : (
            <Button onClick={handlePauseUpload} variant="secondary_s" className="text-sm px-3 h-8">
              {texts?.pause}
            </Button>
          )}
        </div>
      </div>
      {uploadProgress < 100 && (
        <div className="mt-8 flex items-center justify-center gap-1.5 text-xs text-green-600 text-center">
          <CheckCircleIcon className="w-4 h-4 shrink-0" />
          <span>{texts?.resumableHint}</span>
        </div>
      )}
    </div>
  )

  // Logged-in users never see the verify panel; the verified-pill is also
  // gone since it adds noise once we trust the email. On desktop, guests still
  // see the inline verify panel below the file selector. Mobile guests get a
  // dedicated verify-only screen rendered higher up (renders before the
  // file-management view).
  const renderAuthGate = () => {
    if (verified) return null
    // The verify action now lives in the main CTA slot (replacing Create Share
    // Link), so this banner is text-only — white, bold, centered.
    return (
      <div className="mb-3 rounded-2xl border border-bordered px-4 py-2 lg:py-3 flex items-center justify-center gap-2 text-text">
        <EnvelopeIcon className="h-5 w-5 shrink-0" />
        <span className="text-sm font-bold text-center">
          {texts?.verifyEmailToCreate}
        </span>
      </div>
    )
  }

  if (completed && shareToken) {
    const ShareActionButton = ({ icon: Icon, label, onClick }) => (
      <button
        onClick={onClick}
        className="flex flex-col items-center gap-1.5 md:gap-2 bg-primary hover:bg-primary/90 active:bg-primary/80 rounded-2xl p-2.5 md:p-4 transition-colors"
      >
        <Icon className="h-5 w-5 md:h-7 md:w-7 text-white" />
        <span className="text-xs text-white font-medium leading-tight text-center">{label}</span>
      </button>
    )

    const drawerTitle = activePanel === 'email'
      ? texts?.sendOverEmail
      : activePanel === 'qr'
      ? texts?.qrCode
      : ''

    const qrContent = (
      <div className="flex flex-col items-center justify-center flex-1 p-8 gap-4">
        <QRCodeCanvas value={getShareUrl()} size={200} level="H" marginSize={1} />
        <Typography variant="txt2_dim">{texts?.scanQRCode}</Typography>
      </div>
    )

    const handleCopyWithToast = async () => {
      await handleCopy()
      showToast({
        position: TOAST_POSITIONS.TOP_CENTER,
        variant: 'success',
        duration: 2000,
        enableDismissalTracking: false,
        styleOverrides: { progressContainer: 'hidden' },
        message: texts?.linkCopied,
      })
    }

    return (
      <div className="w-full md:max-w-lg md:mx-auto pb-0 md:pb-4 pt-0 md:pt-4 md:px-4">
        <div className="relative bg-paper md:rounded-2xl overflow-hidden flex flex-col shadow-sm">
          {/* Main content */}
          <div className="relative flex flex-col items-center justify-center p-6 md:p-10 text-center gap-5 min-h-[280px] md:min-h-[600px]">
            <span className="h-20 w-20 rounded-full bg-success flex items-center justify-center">
              <CheckIcon className="h-10 w-10 text-white stroke-[2.5]" />
            </span>
            <div>
              <Typography variant="h3">{texts?.shareIsReady}</Typography>
              <Typography variant="txt1_dim" className="block mt-2">
                {texts?.shareIsReadyDesc}
              </Typography>
            </div>

            {accessType === 'authenticated' && accessEmails.length > 0 && (
              <div className="w-full flex flex-col items-center gap-2 mt-4">
                <Typography 
                  variant="body1_accent" 
                  className="mt-0 mb-2"
                >
                  {texts?.invitesSent}
                </Typography>

                <div className="flex flex-wrap justify-center gap-2 max-h-[140px] overflow-y-auto px-2">
                  {accessEmails.map((email) => (
                    <span
                      key={email}
                      className="inline-flex items-center rounded-full bg-neutral_1 border border-bordered px-3 py-1 text-sm text-text"
                    >
                      {email}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Desktop animated drawer — slides up over the main content */}
            <AnimatePresence>
              {!isMobile && activePanel && (
                <motion.div
                  className="absolute inset-0 bg-paper flex flex-col overflow-hidden z-10"
                  initial={{ y: '100%' }}
                  animate={{ y: 0 }}
                  exit={{ y: '100%' }}
                  transition={{ type: 'spring', damping: 28, stiffness: 280 }}
                >
                  <div className="flex items-center justify-between px-5 py-4 border-b border-bordered shrink-0">
                    <Typography variant="txt1_accent">{drawerTitle}</Typography>
                    <button
                      onClick={() => setActivePanel(null)}
                      className="p-1 rounded text-text_secondary hover:text-text transition-colors"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                  <div className="flex-1 overflow-y-auto flex flex-col">
                    {activePanel === 'email' && <LinkSender orderID={orderID} accessType={accessType} />}
                    {activePanel === 'qr' && qrContent}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* 4 share action buttons — always visible at bottom of panel */}
          <div className="relative z-20 grid grid-cols-4 gap-2 md:gap-3 p-3 md:p-4 border-t border-bordered bg-paper">
            <ShareActionButton
              icon={EnvelopeIcon}
              label={texts?.sendOverEmail}
              onClick={() => setActivePanel('email')}
            />
            <ShareActionButton
              icon={ClipboardDocumentIcon}
              label={texts?.copyLink}
              onClick={handleCopyWithToast}
            />
            <ShareActionButton
              icon={QrCodeIcon}
              label={texts?.qrCode}
              onClick={() => setActivePanel('qr')}
            />
            <ShareActionButton
              icon={ShareIcon}
              label={texts?.share}
              onClick={handleNativeShare}
            />
          </div>
        </div>

        {/* Mobile fullscreen modals */}
        {isMobile && (
          <>
            <Modal
              isOpen={activePanel === 'email'}
              onClose={() => setActivePanel(null)}
              variant="transfer_fullscreen"
              title={texts?.sendOverEmail}
            >
              <LinkSender orderID={orderID} accessType={accessType} />
            </Modal>
            <Modal
              isOpen={activePanel === 'qr'}
              onClose={() => setActivePanel(null)}
              variant="transfer_fullscreen"
              title={texts?.qrCode}
            >
              {qrContent}
            </Modal>
          </>
        )}

        <div className="flex gap-6 mt-4 justify-center">
          <Button
            variant="primary_s_black"
            onClick={handleReset}
          >
            {texts?.shareNewFile}
          </Button>
          <Button
            variant="primary_s_black"
            onClick={() => navigate('/account/my-transfers')}
          >
            {texts?.manageShares}
          </Button>
        </div>
      </div>
    )
  }

  // Mobile guests: signin on mobile typically full-page redirects (OAuth),
  // which would wipe any files picked beforehand. Show the verify-only screen
  // ONLY after the user tries to add files (i.e. clicks the add buttons or
  // taps the drop zone) — the landing stays visible until then.
  if (isMobile && !verified && showVerifyPrompt) {
    return (
      <div className="relative max-w-md mx-auto px-4 pt-8 pb-12">
        <div className="bg-paper border border-bordered rounded-3xl p-6 text-center flex flex-col items-center gap-4 shadow-[0_30px_60px_-15px_rgba(15,23,42,0.18),0_10px_24px_-8px_rgba(15,23,42,0.08)] ring-1 ring-black/5">
          <div className="w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center">
            <EnvelopeIcon className="h-7 w-7" />
          </div>
          <Typography variant="h3" className="my-0">
            {texts?.pleaseVerifyEmail}
          </Typography>
          <Typography variant="body2_dim" className="block">
            {texts?.verifyEmailToCreate}
          </Typography>
          <Button
            variant="primary"
            className="w-full max-w-[240px]"
            onClick={() => PubSub.publish("signin", {})}
          >
            {texts?.verifyNow}
          </Button>
        </div>
        <GoPremiumDialog />
      </div>
    )
  }

  // Landing (no files yet): the whole area is the drop zone — tap opens the
  // file picker, drag-drop is accepted. On desktop (mouse), press-and-hold
  // opens the folder picker; that gesture is disabled on touch since
  // webkitdirectory folder selection is unsupported on mobile browsers.
  // Resume-available prompt — modal so the underlay blocks interaction with
  // the landing behind. User must choose Resume or Discard before proceeding.
  const resumeModal = (
    <Modal
      isOpen={!!pendingSession && !resumeMode}
      onClose={() => {}}
      allowCloseBtn={false}
      variant="transfer_compact"
      title={texts?.resumeAvailable}
      footerContent={
        <div className="flex justify-end gap-2 w-full">
          <Button variant="text_btn_ghost" onClick={handleDiscardSession}>
            {texts?.discard}
          </Button>
          <Button variant="primary_s" onClick={enterResumeMode}>
            {texts?.resume}
          </Button>
        </div>
      }
    >
      <div className="flex items-start gap-3">
        <CloudArrowUpIcon className="w-6 h-6 text-primary shrink-0 mt-0.5" />
        <Typography variant="txt1_dim" className="text-text_secondary">
          {(() => {
            const n = pendingSession?.files?.length ?? 0
            const label = `${n} ${n === 1 ? texts?.file : texts?.files}`
            const tmpl = texts?.resumeDesc
            const [before, after] = (tmpl || '').split('{n}')
            return (<>{before}<strong className="text-text font-semibold">{label}</strong>{after}</>)
          })()}
        </Typography>
      </div>
    </Modal>
  )

  if (!hasFiles && !sendNowMode && !resumeMode) {
    return (
      <div className="relative h-full">
        {resumeModal}
        <UploadLanding
          texts={texts}
          creating={creating}
          onPickFiles={() => {
            if (isMobile && !verified) return setShowVerifyPrompt(true)
            if (!creating) addFileInputRef.current?.click()
          }}
          onPickFolders={() => {
            if (isMobile && !verified) return setShowVerifyPrompt(true)
            if (!creating) addFolderInputRef.current?.click()
          }}
          onDrop={(e) => {
            if (isMobile && !verified) { e.preventDefault(); return setShowVerifyPrompt(true) }
            handleFileDrop(e)
          }}
        />
        <div className="hidden">
          <input
            ref={addFileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => { if (e.target.files?.length) handleFilesSelected(e.target.files); e.target.value = '' }}
          />
          <input
            ref={addFolderInputRef}
            type="file"
            webkitdirectory=""
            directory=""
            multiple
            className="hidden"
            onChange={(e) => { if (e.target.files?.length) handleFilesSelected(e.target.files); e.target.value = '' }}
          />
          <input
            ref={resumeInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => { if (e.target.files?.length) handleResumePick(e.target.files); e.target.value = '' }}
          />
          <input
            ref={findFolderInputRef}
            type="file"
            webkitdirectory=""
            directory=""
            multiple
            className="hidden"
            onChange={(e) => { if (e.target.files?.length) handleFindFolderPick(e.target.files); e.target.value = '' }}
          />
        </div>
        <GoPremiumDialog />
      </div>
    )
  }

  return (
    <div className="relative max-w-[960px] h-auto bg-paper border border-bordered rounded-[30px] z-20 p-4 md:p-6 lg:p-8 lg:mt-10 mx-4 md:mx-auto">
      <div className="max-w-7xl mx-auto">

      {/* File / folder management — single screen */}
      <div className="max-w-4xl mx-auto">
        {/* Buttons + added file */}
        <div
          className="pt-6 md:pt-0 mb-4 lg:mb-6"
          onDrop={handleFileDrop}
          onDragOver={(e) => e.preventDefault()}
        >
          {/* Mobile back button — sits in the pt-6 breathing room above the Add buttons */}
          <button
            type="button"
            onClick={handleBack}
            className="md:hidden absolute top-4 left-4 flex items-center justify-center w-8 h-8 rounded-lg text-text_secondary hover:text-text hover:bg-neutral_1/60 transition-colors"
            aria-label="Go back"
          >
            <ChevronLeftIcon className="h-5 w-5 stroke-2" />
          </button>

          {/* Title (desktop, hard-left) + Add files/folders (desktop, hard-right).
              On mobile: buttons stay centered, title hidden as before. */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4 mb-8">
            <button
              type="button"
              onClick={handleBack}
              className="hidden md:flex items-center gap-1 text-text_secondary hover:text-text transition-colors text-lg font-medium"
              aria-label="Go back"
            >
              <ChevronLeftIcon className="h-5 w-5 stroke-2" />
              <span>Back</span>
            </button>

            <Typography variant="h3" className="hidden md:block my-0">
              {texts?.sendFiles}
            </Typography>

            <div className="w-full md:w-auto max-w-[300px] sm:max-w-full md:max-w-auto flex flex-col sm:flex-row items-center justify-center md:justify-end gap-3 flex-nowrap mt-4 md:mt-0 mx-auto sm:mx-0">
              <Button
                variant="primary_s"
                onClick={() => { if (!creating) addFileInputRef.current?.click() }}
                disabled={creating}
                className="w-full sm:w-1/2 sm:max-w-40 md:w-auto"
              >
                <PlusIcon className="w-4 h-4 stroke-[3]"/>
                {texts?.addFiles}
              </Button>

              <Button
                variant="primary_s"
                onClick={() => { if (!creating) addFolderInputRef.current?.click() }}
                disabled={creating}
                className="w-full sm:w-1/2 sm:max-w-40 md:w-auto"
              >
                <PlusIcon className="w-4 h-4 stroke-[3]"/>
                {texts?.addFolder}
              </Button>
            </div>
          </div>

          {/* Totals — counter in resumeMode shows matched/expected plus any
              extras the user added on top of the saved list. */}
          {resumeMode && pendingSession ? (
            <Typography
              variant="txt1_dim"
              className="text-text_secondary block text-right mb-1">
              {(() => {
                const pickedKeys = new Set(files.map(e => `${e.path}:${e.file.size}`))
                const savedKeys = new Set(pendingSession.files.map(f => `${f.path}:${f.size}`))
                const matchedCount = pendingSession.files.filter(f => pickedKeys.has(`${f.path}:${f.size}`)).length
                const total = pendingSession.files.length
                const extras = files.filter(e => !savedKeys.has(`${e.path}:${e.file.size}`))
                const allBytes = pendingSession.files.reduce((s, f) => s + (f.size || 0), 0)
                  + extras.reduce((s, e) => s + (e.file.size || 0), 0)
                const extraSuffix = extras.length ? ` + ${extras.length} ${texts?.added}` : ''
                return `${matchedCount} / ${total}${extraSuffix} · ${formatBytes(allBytes)}`
              })()}
            </Typography>
          ) : hasFiles && (
            <Typography
              variant="txt1_dim"
              className="text-text_secondary block text-right mb-1">
              {files.length} {files.length === 1 ? texts?.file : texts?.files} · {formatBytes(totalSize)}
            </Typography>
          )}

          {/* File/folder tree preview, or empty drop hint */}
          {resumeMode && pendingSession ? (
            (() => {
              // Merge expected (from saved session) + any extras the user
              // added on top. Saved entries that aren't yet picked are
              // flagged "missing"; extras render normally. Both are
              // removable — saved removals prune the session.
              const pickedKeys = new Set(files.map(e => `${e.path}:${e.file.size}`))
              const savedKeys = new Set(pendingSession.files.map(f => `${f.path}:${f.size}`))
              const entries = [
                ...pendingSession.files.map(f => ({ path: f.path, size: f.size })),
                ...files
                  .filter(e => !savedKeys.has(`${e.path}:${e.file.size}`))
                  .map(e => ({ path: e.path, file: e.file })),
              ]
              const statusMap = {}
              let missingCount = 0
              for (const f of pendingSession.files) {
                if (!pickedKeys.has(`${f.path}:${f.size}`)) {
                  statusMap[f.path] = 'missing'
                  missingCount++
                }
              }
              // Live upload status takes precedence over the static "missing"
              // marker — once a file starts uploading we want its progress bar.
              for (const [path, st] of Object.entries(fileStatus)) {
                statusMap[path] = st
              }
              const allMatched = missingCount === 0
              return (
                <>
                  <FileTree
                    entries={entries}
                    statusMap={statusMap}
                    progressMap={fileProgress}
                    onRemove={removeByPathResume}
                    onFindFolder={handleFindFolder}
                    activePath={activeUploadPath}
                    disabled={creating}
                    maxHeightClass="max-h-72"
                  />
                  {!allMatched && (
                    <button
                      type="button"
                      onClick={() => addFileInputRef.current?.click()}
                      disabled={creating}
                      className="mt-3 w-full rounded-xl border-2 border-dashed border-bordered hover:border-primary text-text_secondary hover:text-text py-4 transition-colors disabled:opacity-50"
                    >
                      <Typography variant="txt1_dim">
                        {texts?.attachMatchingFiles}
                      </Typography>
                    </button>
                  )}
                </>
              )
            })()
          ) : hasFiles ? (
            <FileTree
              entries={files}
              onRemove={removeByPath}
              disabled={creating}
              progressMap={fileProgress}
              statusMap={fileStatus}
              activePath={activeUploadPath}
              maxHeightClass="max-h-72"
            />
          ) : (
            <button
              type="button"
              onClick={() => addFileInputRef.current?.click()}
              className="w-full flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-bordered hover:border-primary text-text_secondary hover:text-text py-16 transition-colors"
            >
              <div className="flex items-center gap-2 text-primary">
                <DocumentIcon className="h-7 w-7" />
                <PlusIcon className="h-5 w-5" />
                <FolderIcon className="h-8 w-8" />
              </div>
              <span className="text-sm font-medium">{texts?.addFilesOrFolders}</span>
              <span className="text-xs">{texts?.anyTypeUpTo} <strong>{formatBytes(maxFileSize)}</strong> {texts?.perFile}</span>
            </button>
          )}

          <div className="hidden">
            <input
              ref={addFileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => { if (e.target.files?.length) handleFilesSelected(e.target.files); e.target.value = '' }}
            />
            <input
              ref={addFolderInputRef}
              type="file"
              webkitdirectory=""
              directory=""
              multiple
              className="hidden"
              onChange={(e) => { if (e.target.files?.length) handleFilesSelected(e.target.files); e.target.value = '' }}
            />
            <input
              ref={resumeInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => { if (e.target.files?.length) handleResumePick(e.target.files); e.target.value = '' }}
            />
            <input
              ref={findFolderInputRef}
              type="file"
              webkitdirectory=""
              directory=""
              multiple
              className="hidden"
              onChange={(e) => { if (e.target.files?.length) handleFindFolderPick(e.target.files); e.target.value = '' }}
            />
          </div>

        </div>
      </div>

      {/* Preselected recipients (Send Now / authenticated) — visible below
          the file selector so the user sees who's on the list at a glance.
          Once files are added the auth options section takes over, so hide. */}
      {!hasFiles && accessType === 'authenticated' && accessEmails.length > 0 && (
        <div className="max-w-4xl mx-auto mb-4">
          <div className="flex flex-col-reverse sm:flex-row items-center justify-between gap-4 pb-2 mb-2 sm:mb-0">
            <div className="flex items-center gap-1.5">
              <UsersIcon className="h-4 w-4 text-text_secondary stroke-2" />

              <Typography variant="txt1_accent">
                {texts?.peopleWithAccess}
              </Typography>

              <span className="text-xs text-text_secondary bg-neutral_1 border border-bordered rounded-full px-1.5 py-0.5 leading-none">
                {accessEmails.length}
              </span>
            </div>

            <Button 
              variant="secondary_s" 
              onClick={() => setShowAccessDrawer(true)}
            >
              {texts?.manage}
            </Button>
          </div>

          {/* Emails with access mapped */}
          <div className="flex flex-wrap gap-2">
            {accessEmails.map((email) => (
              <span
                key={email}
                className="inline-flex items-center gap-1.5 rounded-full bg-neutral_1 border border-bordered pl-3 pr-1.5 py-1 text-sm text-text"
              >
                <span className="truncate max-w-[220px]">{email}</span>
                <button
                  onClick={() => setAccessEmails((prev) => prev.filter((e) => e !== email))}
                  className="p-0.5 rounded-full hover:bg-error/10 text-text_secondary hover:text-error transition-colors flex-shrink-0"
                >
                  <XMarkIcon className="h-3.5 w-3.5 stroke-2" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Settings — only once files are added; flies in from the top */}
      {hasFiles && (
        <div className={`max-w-4xl mx-auto animate-fly-in-down${creating ? ' opacity-50 pointer-events-none' : ''}`}>
          <>
            {/* Sharing options tabs (always visible) */}
            <div className="lg:mb-8">
              <div className="relative flex items-center mb-4">
                {/* Left scroll hint — visible when tabs overflow on narrow screens */}
                <button
                  type="button"
                  onClick={() => tabsScrollRef.current?.scrollBy({ left: -150, behavior: 'smooth' })}
                  className="flex-shrink-0 p-1 text-text_secondary hover:text-text lg:hidden"
                  aria-label="Scroll tabs left"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>

                <div
                  ref={tabsScrollRef}
                  className="flex gap-1 overflow-x-auto overflow-y-hidden no-scrollbar flex-nowrap flex-1 min-w-0"
                >
                <button
                  onClick={() => toggleAccessTab('duration')}
                  className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex-shrink-0 ${
                    accessTab === 'duration'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-text_secondary hover:text-text'
                  }`}
                >
                  <CalendarDaysIcon className="h-4 w-4" />
                  {texts?.duration}
                </button>
                <button
                  onClick={() => toggleAccessTab('title')}
                  className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex-shrink-0 ${
                    accessTab === 'title'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-text_secondary hover:text-text'
                  }`}
                >
                  <PencilSquareIcon className="h-4 w-4" />
                  {texts?.title}
                </button>
                <button
                  onClick={() => toggleAccessTab('notes')}
                  className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex-shrink-0 ${
                    accessTab === 'notes'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-text_secondary hover:text-text'
                  }`}
                >
                  <ChatBubbleLeftEllipsisIcon className="h-4 w-4" />
                  {texts?.message}
                </button>
                <button
                  onClick={() => toggleAccessTab('access')}
                  className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex-shrink-0 ${
                    accessTab === 'access'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-text_secondary hover:text-text'
                  }`}
                >
                  <LockClosedIcon className="h-4 w-4" />
                  {texts?.userAccess}
                  {accessEmails.length > 0 && (
                    <span className="text-[11px] text-text_secondary bg-neutral rounded-full px-1.5 py-0.5 leading-none">
                      {accessEmails.length}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => toggleAccessTab('password')}
                  className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex-shrink-0 ${
                    accessTab === 'password'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-text_secondary hover:text-text'
                  }`}
                >
                  <KeyIcon className="h-4 w-4" />
                  {texts?.password}
                  {passwordEnabled && password && <span className="ml-1 w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />}
                </button>
                <button
                  onClick={() => toggleAccessTab('limit')}
                  className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex-shrink-0 ${
                    accessTab === 'limit'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-text_secondary hover:text-text'
                  }`}
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  {texts?.limit}
                </button>
                <button
                  onClick={() => toggleAccessTab('theme')}
                  className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex-shrink-0 ${
                    accessTab === 'theme'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-text_secondary hover:text-text'
                  }`}
                >
                  <SwatchIcon className="h-4 w-4" />
                  {texts?.theme}
                </button>
              </div>

              {/* Right scroll hint */}
              <button
                type="button"
                onClick={() => tabsScrollRef.current?.scrollBy({ left: 150, behavior: 'smooth' })}
                className="flex-shrink-0 p-1 text-text_secondary hover:text-text lg:hidden"
                aria-label="Scroll tabs right"
              >
                <ChevronRightIcon className="h-5 w-5" />
              </button>
            </div>

              {accessTab === 'duration' && (
                <div className="flex flex-wrap gap-2">
                  {retentionOptions.map((opt) => {
                    const locked = opt.days > maxRetention
                    const active = retentionDays === opt.days
                    return (
                      <button
                        key={opt.days}
                        onClick={() => locked ? openGoPremium() : setRetentionDays(opt.days)}
                        className={`relative flex-1 min-w-[28%] rounded-xl border-2 p-3 text-center transition-colors ${
                          active ? opt.activeColor : opt.color
                        }`}
                      >
                        <div className={`font-semibold text-sm ${active ? "text-white" : opt.textColor}`}>{opt.label}</div>
                        {locked && (
                          <div className={`text-xs mt-0.5 ${opt.textColor}`}>🔒</div>
                        )}
                      </button>
                    )
                  })}
                </div>
              )}

              {accessTab === 'title' && (
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={200}
                  autoFocus
                  placeholder={texts?.titlePlaceholder}
                  className="w-full rounded-xl border border-bordered bg-transparent px-4 py-3 text-sm text-text placeholder-gray-400 focus:outline-none focus:border-primary"
                />
              )}

              {accessTab === 'access' && (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    {ACCESS_OPTIONS.map((opt) => {
                      const Icon = opt.icon
                      return (
                        <button
                          key={opt.value}
                          onClick={() => {
                            setAccessType(opt.value)
                            if (opt.value === 'authenticated') setShowAccessDrawer(true)
                          }}
                          className={`w-full rounded-xl border-2 p-3 text-left transition-colors ${
                            accessType === opt.value
                              ? 'border-primary bg-primary/10'
                              : 'border-bordered hover:border-text_secondary'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-0.5">
                            <Icon className="h-4 w-4 text-text_secondary" />
                            <span className="text-sm font-medium text-text">{texts?.[opt.labelKey]}</span>
                          </div>
                          
                          <div className="text-xs text-text_secondary">
                            {texts?.[opt.descKey]}
                          </div>
                        </button>
                      )
                    })}
                  </div>
                  {accessType === 'authenticated' && (
                    <button
                      onClick={() => setShowAccessDrawer(true)}
                      className="mt-3 w-full flex items-center justify-between gap-2 rounded-full border border-primary px-4 py-2.5 text-sm transition-colors"
                    >
                      <div className="flex items-center gap-2 text-primary">
                        <UsersIcon className="hidden sm:block h-4 w-4" />
                        <span>{texts?.manageUserAccess}</span>
                      </div>

                      <span className="text-xs bg-primary rounded-full px-2 py-0.5 text-white font-medium">
                        {accessEmails.length === 0 ? texts?.addUsers : `${accessEmails.length} ${accessEmails.length === 1 ? texts?.user : texts?.users}`}
                      </span>
                    </button>
                  )}
                </>
              )}

              {accessTab === 'password' && (
                <div>
                  <div className="w-full flex items-center justify-between px-1 py-2 text-sm">
                    <div className="flex items-center gap-2">
                      <KeyIcon className="h-4 w-4 text-text_secondary" />
                      <span className="font-medium text-text">{texts?.passwordProtection}</span>
                    </div>
                    <ToggleSwitch
                      isOn={passwordEnabled}
                      onChange={(val) => { setPasswordEnabled(val); if (!val) setPassword('') }}
                    />
                  </div>
                  {passwordEnabled && (
                    <div className="relative mt-2">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={texts?.enterPassword}
                        className="w-full rounded-lg bg-transparent px-3 py-2 pr-10 text-sm text-text placeholder-gray-400 focus:outline-none border border-bordered"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text_secondary hover:text-text"
                      >
                        {showPassword ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {accessTab === 'notes' && (
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  maxLength={2000}
                  rows={5}
                  placeholder={texts?.addMessagePlaceholder}
                  className="w-full rounded-xl border border-bordered bg-transparent px-4 py-3 text-sm text-text placeholder-gray-400 focus:outline-none focus:border-primary resize-none"
                />
              )}

              {accessTab === 'limit' && (
                <div>
                  <Typography variant="txt2_dim" className="block mb-3 text-center">
                    {texts?.limitDesc}
                  </Typography>
                  <input
                    type="number"
                    min="1"
                    value={downloadLimit}
                    onChange={(e) => setDownloadLimit(e.target.value)}
                    placeholder={texts?.eg10}
                    className="w-full rounded-xl border border-bordered bg-transparent px-4 py-3 text-sm text-text placeholder-gray-400 focus:outline-none focus:border-primary"
                  />
                </div>
              )}

              {accessTab === 'theme' && (
                <div>
                  <Typography variant="txt2_dim" className="block mb-3 text-center">
                    {texts?.themeDesc}
                  </Typography>
                  <ThemeSelect value={selectedThemeId} onChange={setSelectedThemeId} />
                </div>
              )}
            </div>

          </>
        </div>
      )}
      </div>

      {/* Inline button / status - desktop only */}
      {(hasFiles || resumeMode) && !completed && (
        <div className="mt-8 mx-auto">
          {(creating || paused) ? (
            renderCreateProgress()
          ) : (
            <>
              {renderAuthGate()}
              {verified ? (
                <Button
                  variant="primary"
                  onClick={handleShare}
                  disabled={resumeMode && !hasFiles}
                  className="w-full max-w-[280px] !h-[60px] mt-8 mx-auto"
                >
                  {resumeMode
                    ? texts?.resumeUpload
                    : texts?.createShareLink}
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={() => PubSub.publish("signin", {})}
                  className="w-full max-w-[280px] !h-[60px] mt-8 mx-auto"
                >
                  {texts?.verifyNow}
                </Button>
              )}
              {resumeMode && (
                <div className="flex gap-6 mt-6 justify-center">
                  <Button
                    variant="primary_s_black"
                    onClick={async () => {
                      const ok = await showPromptDialogAsync({
                        title: texts?.shareNewFile,
                        message: texts?.discardResumeForNewShare,
                        yesLabel: texts?.yes,
                        noLabel: texts?.cancel,
                      })
                      if (!ok) return
                      if (pendingSession?.orderID) {
                        ShareAPI.DeleteFileShare(pendingSession.orderID).catch(() => {})
                      }
                      TusUploader.discardFingerprints(files.map(e => e.file)).catch(() => {})
                      Session.clear()
                      pendingSessionRef.current = null
                      setPendingSession(null)
                      setOrderID(null)
                      setResumeMode(false)
                      setFiles([])
                      setFileProgress({})
                      setFileStatus({})
                      setActiveUploadPath(null)
                    }}
                  >
                    {texts?.shareNewFile}
                  </Button>
                  <Button
                    variant="primary_s_black"
                    onClick={async () => {
                      const ok = await showPromptDialogAsync({
                        title: texts?.manageShares,
                        message: texts?.discardResumeForManage,
                        yesLabel: texts?.yes,
                        noLabel: texts?.cancel,
                      })
                      if (!ok) return
                      if (pendingSession?.orderID) {
                        ShareAPI.DeleteFileShare(pendingSession.orderID).catch(() => {})
                      }
                      TusUploader.discardFingerprints(files.map(e => e.file)).catch(() => {})
                      Session.clear()
                      navigate('/account/my-transfers')
                    }}
                  >
                    {texts?.manageShares}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Access drawer — slides up from the bottom, clipped inside the outer
          Share container and sized to fill it. */}
      <AnimatePresence>
        {showAccessDrawer && (
          <div className="absolute inset-0 z-[60] overflow-hidden rounded-[30px]">
            <motion.div
              className="absolute inset-0 bg-black/20"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={() => setShowAccessDrawer(false)}
            />

            <motion.div
              className="absolute inset-0 bg-paper rounded-[30px] flex flex-col shadow-2xl"
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'tween', duration: 0.3, ease: 'easeOut' }}
            >
              <div className="flex items-center gap-1.5 px-5 py-2 md:py-4 border-b border-bordered shrink-0">
                <UsersIcon className="h-4 w-4 text-text_secondary stroke-2" />
                <Typography variant="txt_accent">
                  {texts?.userAccess}
                </Typography>
                {accessEmails.length > 0 && (
                  <span className="text-xs text-text_secondary bg-neutral_1 border border-bordered rounded-full px-1.5 py-0.5 leading-none">
                    {accessEmails.length}
                  </span>
                )}

                <Button
                  variant="close_icon"
                  className="ml-auto"
                  onClick={() => setShowAccessDrawer(false)}
                >
                  <XMarkIcon className="h-5 w-5" />
                </Button>
              </div>

              <div className="flex-1 overflow-y-auto p-5">
                <UserAccessWidget
                  hideHeader
                  emails={accessEmails}
                  maxEmails={maxAccessEmails}
                  onUpgrade={openGoPremium}
                  onAdd={async (email) => setAccessEmails((prev) => {
                    if (prev.includes(email)) return prev
                    if (maxAccessEmails > 0 && prev.length >= maxAccessEmails) return prev
                    return [...prev, email]
                  })}
                  onRemove={async (email) => setAccessEmails((prev) => prev.filter((e) => e !== email))}
                />
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-5 border-t border-bordered shrink-0">
                <div className="w-full sm:w-auto flex gap-2">
                  <Button
                    variant="secondary"
                    onClick={handleClearAccessAll}
                    disabled={!accessEmails.length}
                    className="flex-1"
                  >
                    {texts?.clearAll}
                  </Button>
                  
                  <Button
                    variant="secondary"
                    onClick={() => { setSaveGroupName(''); setSaveGroupOpen(true) }}
                    disabled={!accessEmails.length}
                    className="flex-1"
                  >
                    {texts?.saveAsGroup}
                  </Button>
                </div>

                <Button 
                  variant="primary" 
                  onClick={() => setShowAccessDrawer(false)}
                  className="w-full sm:w-auto"
                >
                  {texts?.done}
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <Modal
        isOpen={saveGroupOpen}
        onClose={() => { if (!savingGroup) { setSaveGroupOpen(false); setSaveGroupName('') } }}
        title={texts?.saveAsGroup}
        variant="compact"
        className="max-w-sm"
        closeOnClickOutside
        closeOnEscape
        zIndex={600}
      >
        <div className="p-5 flex flex-col gap-4">
          <Input
            variant="transfer_compact"
            autoFocus
            value={saveGroupName}
            onChange={(e) => setSaveGroupName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && saveGroupName.trim()) handleSubmitSaveGroup() }}
            placeholder={texts?.groupNamePlaceholder}
            className="w-full"
          />
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => { setSaveGroupOpen(false); setSaveGroupName('') }}
              disabled={savingGroup}
              className="flex-1"
            >
              {texts?.cancel}
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmitSaveGroup}
              disabled={savingGroup || !saveGroupName.trim()}
              className="flex-1"
            >
              {savingGroup ? <Spinner className="w-[14px] h-[14px]" /> : texts?.save}
            </Button>
          </div>
        </div>
      </Modal>

      <GoPremiumDialog />
    </div>
  )
}
